import type { Theme, SxProps } from '@mui/material/styles';

import { useRef } from 'react';
import { useTabs } from 'minimal-shared/hooks';

import Grid from '@mui/material/Grid2';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import { Box, Stack, Divider, Typography } from '@mui/material';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { Iconify } from 'src/components/iconify';
import { EmptyContent } from 'src/components/empty-content';

import { ProductDetailsSkeleton } from '../product-skeleton';
import { ProductDetailsReview } from '../product-details-review';
import { ProductDetailsSummary } from '../product-details-summary';
import { ProductDetailsCarousel } from '../product-details-carousel';
import { ProductExteriorOrganizer } from '../product-exterior-organizer';
import { ProductDetailsDescription } from '../product-details-description';
import { ProductSocialInfoForBlog } from '../product-social-info-for-blog';

// ----------------------------------------------------------------------

type Props = {
  product?: any;
  loading?: boolean;
  error?: any | undefined;
  data?: any;
  entityType: string;
};

export function ProductShopDetailsView({ product, error, loading, data, entityType }: Props) {
  const containerStyles: SxProps<Theme> = {
    mt: 5,
    mb: 10,
  };

  const tabs = useTabs('description');
  const reviewsTargetRef = useRef<HTMLDivElement>(null);

  if (loading) {
    return (
      <Container sx={containerStyles}>
        <ProductDetailsSkeleton />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={containerStyles}>
        <EmptyContent
          filled
          title={entityType === 'blog' ? 'İçerik bulunamadı' : 'Aktivite bulunamadı'}
          description="Aradığınız sayfa kaldırılmış ya da bağlantı hatalı olabilir."
          action={
            <Button
              component={RouterLink}
              href={paths.aktivite.root}
              variant="contained"
              startIcon={<Iconify width={16} icon="eva:arrow-ios-back-fill" />}
              sx={{ mt: 3 }}
            >
              Güncel aktivitelere göz at
            </Button>
          }
          sx={{ py: 10 }}
        />
      </Container>
    );
  }

  const isPast = entityType === 'aktivite' && Boolean(product?.isPast);
  const rawCategory = product?.listing?.category;
  const category = Array.isArray(rawCategory) ? rawCategory[0] : rawCategory;

  return (
    <Container sx={containerStyles}>
      {isPast && (
        <Box
          role="status"
          sx={{
            mb: 4,
            p: { xs: 2, md: 2.5 },
            gap: 2,
            display: 'flex',
            alignItems: 'flex-start',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'warning.main',
            bgcolor: 'warning.lighter',
            color: 'warning.darker',
          }}
        >
          <Iconify icon="solar:calendar-linear" width={28} sx={{ flexShrink: 0, mt: 0.25 }} />
          <Box>
            <Typography variant="subtitle1" fontFamily="Rubik" fontWeight={600}>
              Bu aktivite sona erdi
            </Typography>
            <Typography variant="body2" fontFamily="Rubik">
              Bu aktivitenin tarihi geçtiği için rezervasyon alınmıyor. Benzer güncel aktivitelere
              göz atabilirsiniz.
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1.5 }}>
              {category && (
                <Button
                  component={RouterLink}
                  href={paths.aktivite.list({ category })}
                  variant="contained"
                  color="warning"
                  size="small"
                >
                  Güncel {category} aktiviteleri
                </Button>
              )}
              <Button
                component={RouterLink}
                href={paths.aktivite.root}
                variant="outlined"
                color="inherit"
                size="small"
              >
                Tüm güncel aktiviteler
              </Button>
            </Stack>
          </Box>
        </Box>
      )}

      <Grid container spacing={{ xs: 3, md: 5 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <ProductDetailsCarousel
            images={
              product?.listing?.images && product?.listing?.images.length > 0
                ? [product?.listing?.coverImage].concat(product?.listing?.images)
                : [product?.listing?.coverImage]
            }
            product={product}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          {product && (
            <ProductDetailsSummary
              product={product}
              data={data}
              entityType={entityType}
              setReviewTabActive={() => {
                tabs.setValue('reviews');

                if (reviewsTargetRef.current) {
                  setTimeout(() => {
                    reviewsTargetRef.current?.scrollIntoView({
                      behavior: 'smooth',
                      block: 'center',
                    });
                  }, 100);
                }
              }}
            />
          )}
        </Grid>
      </Grid>

      {/* <Tabs
        ref={reviewsTargetRef}
        value={tabs.value}
        onChange={tabs.onChange}
        TabIndicatorProps={{
          style: {
            backgroundColor: '#7DC9CF',
            height: '6px',
            borderRadius: 1,
          },
        }}
        sx={[
          (theme) => ({
            px: 3,
            mt: 3,
            boxShadow: `inset 0 -2px 0 0 ${varAlpha(theme.vars.palette.grey['500Channel'], 0.08)}`,
          }),
        ]}
      >
        {[
          { value: 'description', label: 'Etkinlik Detayları' },
          { value: 'reviews', label: `Yorumlar (${product?.listing?.reviews.count})` },
          { value: 'exteriorOrganizer', label: 'Organizatör' },
        ].map((tab) => (
          <Tab key={tab.value} value={tab.value} label={tab.label} />
        ))}
      </Tabs>

      {tabs.value === 'description' && <ProductDetailsDescription product={product} />}

      {tabs.value === 'reviews' && (
        <ProductDetailsReview
          ratings={product?.ratings || []}
          reviews={product?.listing?.reviews.data}
          totalRatings={product?.totalRatings || 0}
          totalReviews={product?.listing?.reviews.count}
          listingId={product?.listing?._id}
          activityId={product?._id}
        />
      )}

      {tabs.value === 'exteriorOrganizer' && (
        <ProductExteriorOrganizer product={product} data={data} />
      )} */}

      <Divider sx={{ borderStyle: 'dashed', pt: 4 }} />

      <ProductDetailsDescription product={product} entityType={entityType} />

      <Divider sx={{ borderStyle: 'dashed' }} />

      {entityType === 'aktivite' && <ProductExteriorOrganizer product={product} data={data} />}
      {entityType === 'blog' && <ProductSocialInfoForBlog product={product} data={data} />}

      <Divider sx={{ borderStyle: 'dashed' }} />
      <Box ref={reviewsTargetRef} />

      <ProductDetailsReview
        ratings={product?.ratings || []}
        reviews={product?.listing?.reviews?.data}
        totalRatings={product?.totalRatings || 0}
        totalReviews={product?.listing?.reviews?.count}
        listingId={product?.listing?._id}
        activityId={product?._id}
        entityType={entityType}
      />
    </Container>
  );
}
